<template>
  <VirtualScroller
    :items="items"
    :itemSize="itemSize"
    :scrollHeight="scrollHeight"
    :style="scrollerStyle"
    :loading="loading"
    :showLoader="showLoader"
    :delay="delay"
    :lazy="lazy"
    :class="['mop-virtual-scroller', className]"
    v-bind="$attrs"
    @scroll="$emit('scroll', $event)"
  >
    <template #content="slotProps">
      <slot name="content" v-bind="slotProps">
        <template v-if="items.length === 0">
          <div class="mop-empty-state">
            <div class="empty-state-content">
              <i :class="[emptyIcon, 'empty-state-icon']" :aria-hidden="true"></i>
              <h3 class="empty-state-title">{{ emptyTitle }}</h3>
              <p class="empty-state-message">{{ emptyMessage }}</p>
              <MOPButton
                v-if="showRefresh"
                :label="t('common.refresh')"
                icon="pi pi-refresh"
                @click="$emit('refresh')"
                severity="secondary"
                size="small"
                :aria-label="t('common.refresh')"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <slot name="item" v-for="(item, index) in items" :key="index" :item="item" :index="index"></slot>
        </template>
      </slot>
    </template>

    <template #item="slotProps">
      <div :style="{ height: `${itemSize}px` }" class="mop-virtual-scroller-item">
        <slot name="item" v-bind="slotProps"></slot>
      </div>
    </template>

    <template #loader>
      <slot name="loader">
        <div class="loading-skeleton">
          <div v-for="n in 5" :key="n" class="skeleton-item"></div>
        </div>
      </slot>
    </template>
  </VirtualScroller>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import VirtualScroller from 'primevue/virtualscroller'

defineOptions({
  name: 'MOPVirtualScroller'
})

interface Props {
  items: any[]
  itemSize: number
  scrollHeight?: string
  loading?: boolean
  showLoader?: boolean
  delay?: number
  lazy?: boolean
  className?: string
  style?: Record<string, any>
  emptyTitle?: string
  emptyMessage?: string
  emptyIcon?: string
  showRefresh?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  showLoader: false,
  delay: 0,
  lazy: false,
  className: '',
  style: () => ({}),
  emptyTitle: '',
  emptyMessage: '',
  emptyIcon: 'pi pi-inbox',
  showRefresh: false
})

const { t } = useI18n()

defineEmits<{
  (e: 'scroll', event: Event): void
  (e: 'refresh'): void
}>()

// Computed styles
const scrollerStyle = computed(() => ({
  ...props.style,
  height: props.scrollHeight
}))
</script>

<style lang="scss">
.mop-virtual-scroller {
  // Base styles
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: auto;
  contain: strict;

  // Scrollbar styling
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--surface-400);
    border-radius: 3px;

    &:hover {
      background-color: var(--surface-500);
    }
  }

  &::-webkit-scrollbar-track {
    background-color: var(--surface-100);
  }

  // Content container
  .p-virtualscroller-content {
    display: block;
    contain: content;
    will-change: transform;
  }

  // Item styling
  .mop-virtual-scroller-item {
    position: relative;
    contain: content;
    backface-visibility: hidden;
  }

  // Loading state
  &.p-virtualscroller-loading {
    .p-virtualscroller-loader {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--surface-ground);
      opacity: 0.7;
      z-index: 1;
    }
  }

  // Loading skeleton
  .loading-skeleton {
    padding: 1rem;
  }

  .skeleton-item {
    height: 100px;
    background: var(--surface-200);
    margin-bottom: 0.5rem;
    border-radius: var(--border-radius);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }

  // Empty state
  .mop-empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    background: var(--surface-card);
    border-radius: var(--border-radius);
    min-height: 200px;

    .empty-state-content {
      max-width: 300px;
    }

    .empty-state-icon {
      font-size: 3rem;
      color: var(--text-color-secondary);
      margin-bottom: 1rem;
    }

    .empty-state-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--text-color);
      margin: 0 0 0.5rem 0;
    }

    .empty-state-message {
      color: var(--text-color-secondary);
      margin: 0 0 1.5rem 0;
      line-height: 1.5;
    }
  }
}

// Touch device optimizations
@media (hover: none) {
  .mop-virtual-scroller {
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;

    .mop-virtual-scroller-item {
      touch-action: pan-y;
    }

    .p-button {
      min-height: 2.5rem;
      min-width: 2.5rem;
    }
  }
}

// High contrast mode
@media (forced-colors: active) {
  .mop-virtual-scroller {
    border: 1px solid CanvasText;

    &::-webkit-scrollbar-thumb {
      background-color: ButtonText;
    }

    &::-webkit-scrollbar-track {
      background-color: Canvas;
    }

    .mop-empty-state {
      border: 1px solid CanvasText;

      .empty-state-icon,
      .empty-state-title,
      .empty-state-message {
        color: CanvasText;
      }
    }
  }
}
</style>
